// src/pages/Subscription/TenantSubscriptionPage.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Button,
  Spinner,
  Progress,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import { get, post } from 'helpers/api_helper';
import { showSuccessAlert, showErroeAlert } from 'pages/utils/alertMessages';
import moment from 'moment';
import {
  RiShieldCheckLine,
  RiVipCrownLine,
  RiBankCardLine,
  RiCheckboxCircleLine,
  RiCloseCircleLine,
  RiTimeLine,
  RiRefreshLine,
  RiArrowRightLine,
  RiFlashlightLine,
  RiUserHeartLine,
  RiBuildingLine,
  RiFileDownloadLine,
  RiLock2Line,
  RiSecurePaymentLine,
  RiAlertLine,
  RiQuestionLine,
} from 'react-icons/ri';
import './subscription.scss';

const TenantSubscriptionPage = () => {
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' | 'yearly'
  
  // Checkout Modal State
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isCheckoutModal, setIsCheckoutModal] = useState(false);
  const [enableAutoDebit, setEnableAutoDebit] = useState(true);
  const [initiatingPayment, setInitiatingPayment] = useState(false);

  // Billing Contact Form
  const [billingContact, setBillingContact] = useState({
    billing_name: '',
    billing_email: '',
    billing_phone: '',
  });

  // Invoice / Payment Receipt Modal
  const [selectedPayment, setSelectedPayment] = useState(null);

  const fetchSubscriptionDetails = async () => {
    setLoading(true);
    try {
      const res = await get('/subscriptions/details');
      if (res?.success && res?.body) {
        setSubscriptionData(res.body);
        if (res.body.clinic) {
          setBillingContact({
            billing_name: res.body.clinic.clinic_name || '',
            billing_email: res.body.clinic.email || '',
            billing_phone: res.body.clinic.phone || '',
          });
        }
      }
    } catch (err) {
      console.warn('Error fetching subscription details:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionDetails();
  }, []);

  const openCheckout = (plan) => {
    setSelectedPlan(plan);
    setIsCheckoutModal(true);
  };

  // Submit to PayU Gateway
  const handleProceedPayU = async () => {
    if (!selectedPlan) return;
    setInitiatingPayment(true);
    try {
      const payload = {
        plan_id: selectedPlan.id,
        billing_cycle: billingCycle,
        enable_recurring: enableAutoDebit,
        billing_name: billingContact.billing_name,
        billing_email: billingContact.billing_email,
        billing_phone: billingContact.billing_phone,
      };

      const res = await post('/subscriptions/initiate-payment', payload);
      if (res?.success && res?.body?.action_url && res?.body?.payload) {
        const { action_url, payload: payuPayload } = res.body;

        // Dynamically build and submit hidden POST form to PayU India Gateway
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = action_url;
        form.style.display = 'none';

        Object.keys(payuPayload).forEach((key) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = payuPayload[key] !== null && payuPayload[key] !== undefined ? payuPayload[key] : '';
          form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();
      } else {
        showErroeAlert(res?.message || 'Failed to initiate PayU payment session.');
        setInitiatingPayment(false);
      }
    } catch (err) {
      showErroeAlert('PayU checkout error: ' + err.message);
      setInitiatingPayment(false);
    }
  };

  const handleCancelAutoRenew = async () => {
    if (!window.confirm('Are you sure you want to cancel automatic renewal for your subscription?')) return;
    try {
      const res = await post('/subscriptions/cancel-auto-renew', {});
      if (res?.success) {
        showSuccessAlert('Auto-renewal mandate cancelled.');
        fetchSubscriptionDetails();
      } else {
        showErroeAlert(res?.message || 'Failed to cancel auto-renewal.');
      }
    } catch (err) {
      showErroeAlert('Error: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="page-content py-5 text-center">
        <Spinner color="primary" size="lg" />
        <p className="mt-3 text-muted font-size-14">Loading Clinic SaaS Subscription Center...</p>
      </div>
    );
  }

  const activeSub = subscriptionData?.active_subscription || subscriptionData?.subscription || null;
  const rawPlans = subscriptionData?.available_plans || subscriptionData?.plans || [];
  
  const defaultFallbackPlans = [
    {
      id: 1,
      name: 'Free Trial',
      slug: 'free-trial',
      description: '14-day full access trial for new dental practices to explore all clinical features.',
      price_monthly: 0,
      price_yearly: 0,
      max_chairs: 1,
      max_doctors: 1,
      max_patients: 100,
      is_popular: 0,
    },
    {
      id: 2,
      name: 'Standard Starter Clinic',
      slug: 'starter-clinic',
      description: 'Essential clinical and OPD tools for solo practitioners and boutique dental practices.',
      price_monthly: 1499,
      price_yearly: 14990,
      max_chairs: 2,
      max_doctors: 2,
      max_patients: 1000,
      is_popular: 0,
    },
    {
      id: 3,
      name: 'Growth Dental OPD',
      slug: 'growth-opd',
      description: 'Full-featured clinic suite with multi-chair OPD queues, WhatsApp automation & reports.',
      price_monthly: 2999,
      price_yearly: 29990,
      max_chairs: 5,
      max_doctors: 5,
      max_patients: 10000,
      is_popular: 1,
    },
    {
      id: 4,
      name: 'Enterprise Multi-Branch',
      slug: 'enterprise',
      description: 'Unlimited chairs, doctors, custom dental chart templates, advanced analytics and priority 24/7 support.',
      price_monthly: 5999,
      price_yearly: 59990,
      max_chairs: 'Unlimited',
      max_doctors: 'Unlimited',
      max_patients: 'Unlimited',
      is_popular: 0,
    },
  ];

  const availablePlans = rawPlans.length > 0 ? rawPlans : defaultFallbackPlans;
  const currentPlan = activeSub?.plan || availablePlans.find(p => p.id === activeSub?.plan_id) || subscriptionData?.current_plan || availablePlans[1];
  const paymentHistory = subscriptionData?.payment_history || [];
  const limits = subscriptionData?.limits || {};
  const isSubExpired = Boolean(
    activeSub && (
      activeSub.status === 'expired' ||
      (activeSub.expiry_date && new Date() > new Date(activeSub.expiry_date))
    )
  );

  const calculatePrice = (plan) => {
    if (!plan) return 0;
    if (billingCycle === 'yearly') {
      const annual = Number(plan.price_yearly || plan.annual_price || plan.price_annual || (parseFloat(plan.price_monthly || plan.price || 0) * 10));
      return Math.round(annual);
    }
    return Number(plan.price_monthly || plan.monthly_price || plan.price || 0);
  };

  const getTaxBreakdown = (basePrice) => {
    const gst = Math.round(basePrice * 0.18);
    const total = basePrice + gst;
    return { basePrice, gst, total };
  };

  return (
    <div className="page-content tenant-subscription-page">
      <Container fluid>
        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
          <div>
            <h4 className="font-size-20 font-weight-bold text-dark mb-1 d-flex align-items-center gap-2">
              <RiVipCrownLine className="text-warning font-size-24" />
              Clinic SaaS Subscription & Billing
            </h4>
            <p className="text-muted font-size-13 mb-0">
              Manage your Oralstop Dental OPD SaaS plan, chair quotas, and PayU recurring subscriptions.
            </p>
          </div>

          <div className="d-flex align-items-center gap-2">
            <Button
              color="light"
              size="sm"
              onClick={fetchSubscriptionDetails}
              className="d-flex align-items-center gap-1"
            >
              <RiRefreshLine /> Refresh
            </Button>
            <Badge color="primary" className="px-3 py-2 font-size-12">
              <RiBankCardLine className="me-1" /> Powered by PayU India
            </Badge>
          </div>
        </div>

        {/* Current Active Plan Banner */}
        <Card className="border-0 shadow-sm mb-4 bg-gradient-primary text-white overflow-hidden position-relative">
          <CardBody className="p-4">
            <Row className="align-items-center">
              <Col md={7}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="badge bg-white text-primary font-size-12 font-weight-bold px-2.5 py-1">
                    ACTIVE PLAN
                  </span>
                  {activeSub?.status === 'active' && !isSubExpired ? (
                    <span className="badge bg-success font-size-12 px-2.5 py-1 d-flex align-items-center gap-1">
                      <RiCheckboxCircleLine /> Active
                    </span>
                  ) : (
                    <span className="badge bg-danger font-size-12 px-2.5 py-1">
                      {isSubExpired ? 'EXPIRED' : (activeSub?.status || 'TRIAL')}
                    </span>
                  )}
                </div>

                <h3 className="text-white font-weight-bold mb-1">
                  {currentPlan?.name || 'Standard Starter Clinic'}
                </h3>
                <p className="text-white-50 font-size-13 mb-3">
                  {currentPlan?.description || 'Essential clinical tools for growing dental practices.'}
                </p>

                <div className="d-flex flex-wrap gap-4 text-white font-size-13">
                  <div>
                    <span className="text-white-50 d-block font-size-11">BILLING CYCLE</span>
                    <strong>{activeSub?.billing_cycle?.toUpperCase() || 'MONTHLY'}</strong>
                  </div>
                  <div>
                    <span className="text-white-50 d-block font-size-11">VALID UNTIL</span>
                    <strong>
                      {activeSub?.end_date
                        ? moment(activeSub.end_date).format('DD MMM YYYY')
                        : 'Lifetime / Trial'}
                    </strong>
                  </div>
                  <div>
                    <span className="text-white-50 d-block font-size-11">AUTO RENEWAL</span>
                    <strong>{activeSub?.auto_renew ? 'Enabled (PayU SI)' : 'Disabled (Manual)'}</strong>
                  </div>
                </div>
              </Col>

              <Col md={5} className="mt-4 mt-md-0 border-start-md border-white-50 ps-md-4">
                <h6 className="text-white-50 font-size-12 text-uppercase mb-3">Plan Usage & Resource Quotas</h6>
                
                {/* Chairs Usage */}
                <div className="mb-2">
                  <div className="d-flex justify-content-between font-size-12 mb-1">
                    <span>Dental Chairs</span>
                    <strong>
                      {limits.chairs_used || 1} / {limits.max_chairs || currentPlan?.max_chairs || 'Unlimited'}
                    </strong>
                  </div>
                  <Progress
                    value={limits.max_chairs ? ((limits.chairs_used || 1) / limits.max_chairs) * 100 : 30}
                    color="warning"
                    style={{ height: 6 }}
                  />
                </div>

                {/* Doctors Usage */}
                <div className="mb-2">
                  <div className="d-flex justify-content-between font-size-12 mb-1">
                    <span>Doctor Accounts</span>
                    <strong>
                      {limits.doctors_used || 1} / {limits.max_doctors || currentPlan?.max_doctors || 'Unlimited'}
                    </strong>
                  </div>
                  <Progress
                    value={limits.max_doctors ? ((limits.doctors_used || 1) / limits.max_doctors) * 100 : 25}
                    color="info"
                    style={{ height: 6 }}
                  />
                </div>

                {activeSub?.auto_renew && (
                  <div className="mt-3 text-end">
                    <Button
                      color="light"
                      outline
                      size="sm"
                      className="text-white border-white font-size-11"
                      onClick={handleCancelAutoRenew}
                    >
                      Cancel Auto-Renewal
                    </Button>
                  </div>
                )}
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Pricing & Plan Upgrades Selection */}
        <div className="text-center mb-4 mt-5">
          <h4 className="font-weight-bold text-dark mb-2">Upgrade or Renew Your Dental Clinic Tier</h4>
          <p className="text-muted font-size-14 max-w-600 mx-auto">
            Choose the subscription tier tailored to your dental clinic operations. Includes instant access, multi-chair support, and WhatsApp/Email automation.
          </p>

          {/* Billing Cycle Switcher */}
          <div className="d-inline-flex align-items-center bg-white p-1.5 rounded-pill shadow-sm border mt-2">
            <button
              type="button"
              className={`btn btn-sm rounded-pill px-4 ${
                billingCycle === 'monthly' ? 'btn-primary' : 'btn-light text-muted'
              }`}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly Billing
            </button>
            <button
              type="button"
              className={`btn btn-sm rounded-pill px-4 position-relative ${
                billingCycle === 'yearly' ? 'btn-primary' : 'btn-light text-muted'
              }`}
              onClick={() => setBillingCycle('yearly')}
            >
              Annual Billing
              <span className="badge bg-success ms-1.5 font-size-10">Save 20%</span>
            </button>
          </div>
        </div>

        {/* Available Plans Grid */}
        <Row className="g-4 mb-5 justify-content-center">
          {availablePlans.map((plan) => {
            const isCurrent = currentPlan?.id === plan.id;
            const price = calculatePrice(plan);
            const isPopular = plan.slug === 'pro' || plan.name?.toLowerCase().includes('pro');

            return (
              <Col key={plan.id} lg={4} md={6}>
                <Card
                  className={`h-100 border transition-hover shadow-sm ${
                    isPopular ? 'border-primary border-2' : ''
                  }`}
                  style={{ borderRadius: 12 }}
                >
                  {isPopular && (
                    <div className="position-absolute top-0 end-0 bg-primary text-white font-size-11 px-3 py-1 rounded-bl-3 font-weight-bold">
                      MOST POPULAR
                    </div>
                  )}
                  <CardBody className="p-4 d-flex flex-column">
                    <div className="mb-3">
                      <h5 className="font-weight-bold text-dark mb-1">{plan.name}</h5>
                      <p className="text-muted font-size-12 mb-0" style={{ minHeight: 36 }}>
                        {plan.description || 'Comprehensive management package for dental teams.'}
                      </p>
                    </div>

                    <div className="my-3 pb-3 border-bottom">
                      <div className="d-flex align-items-baseline">
                        <span className="font-size-24 font-weight-bold text-dark">₹ {price.toLocaleString('en-IN')}</span>
                        <span className="text-muted font-size-13 ms-1">
                          / {billingCycle === 'yearly' ? 'year' : 'month'} + GST
                        </span>
                      </div>
                      {billingCycle === 'yearly' && (
                        <small className="text-success font-size-12 d-block mt-1">
                          Equivalent to ₹ {Math.round(price / 12).toLocaleString('en-IN')}/mo
                        </small>
                      )}
                    </div>

                    {/* Features & Quota List */}
                    <div className="flex-grow-1 mb-4">
                      <h6 className="font-size-12 text-muted text-uppercase font-weight-bold mb-2">
                        Plan Inclusions:
                      </h6>
                      <ul className="list-unstyled font-size-13 mb-0">
                        <li className="d-flex align-items-center gap-2 mb-2">
                          <RiCheckboxCircleLine className="text-success font-size-16 flex-shrink-0" />
                          <span>
                            <strong>{plan.max_chairs || 'Unlimited'}</strong> Dental Chairs
                          </span>
                        </li>
                        <li className="d-flex align-items-center gap-2 mb-2">
                          <RiCheckboxCircleLine className="text-success font-size-16 flex-shrink-0" />
                          <span>
                            <strong>{plan.max_doctors || 'Unlimited'}</strong> Doctor Accounts
                          </span>
                        </li>
                        <li className="d-flex align-items-center gap-2 mb-2">
                          <RiCheckboxCircleLine className="text-success font-size-16 flex-shrink-0" />
                          <span>
                            <strong>{plan.max_patients || 'Unlimited'}</strong> Patient Records
                          </span>
                        </li>
                        <li className="d-flex align-items-center gap-2 mb-2">
                          <RiCheckboxCircleLine className="text-success font-size-16 flex-shrink-0" />
                          <span>WhatsApp & Email Reminders</span>
                        </li>
                        <li className="d-flex align-items-center gap-2 mb-2">
                          <RiCheckboxCircleLine className="text-success font-size-16 flex-shrink-0" />
                          <span>OPD Queue & Electronic Health Records</span>
                        </li>
                      </ul>
                    </div>

                    <div>
                      {isCurrent && !isSubExpired ? (
                        <Button color="light" block disabled className="font-weight-bold">
                          Current Active Plan
                        </Button>
                      ) : isCurrent && isSubExpired ? (
                        <Button
                          color="danger"
                          block
                          className="font-weight-bold py-2 d-flex align-items-center justify-content-center gap-1 shadow-sm"
                          onClick={() => openCheckout(plan)}
                        >
                          <RiFlashlightLine /> Renew Subscription
                        </Button>
                      ) : (
                        <Button
                          color={isPopular ? 'primary' : 'outline-primary'}
                          block
                          className="font-weight-bold py-2 d-flex align-items-center justify-content-center gap-1"
                          onClick={() => openCheckout(plan)}
                        >
                          <RiFlashlightLine /> Upgrade with PayU
                        </Button>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </Col>
            );
          })}
        </Row>

        {/* Payment History & Tax Invoices Table */}
        <Card className="border shadow-sm mb-4">
          <CardHeader className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h5 className="font-weight-bold mb-0 text-dark d-flex align-items-center gap-1.5">
                <RiBankCardLine className="text-primary font-size-18" />
                Subscription Payment History & Tax Invoices
              </h5>
              <small className="text-muted">
                Official PayU transaction records, invoices, and payment receipts.
              </small>
            </div>
          </CardHeader>
          <CardBody className="p-0">
            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead className="table-light font-size-12 text-muted">
                  <tr>
                    <th>DATE</th>
                    <th>TRANSACTION ID</th>
                    <th>PLAN</th>
                    <th>CYCLE</th>
                    <th>AMOUNT</th>
                    <th>METHOD</th>
                    <th>STATUS</th>
                    <th className="text-end">RECEIPT</th>
                  </tr>
                </thead>
                <tbody className="font-size-13">
                  {paymentHistory.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-4 text-muted">
                        No previous subscription payments found.
                      </td>
                    </tr>
                  ) : (
                    paymentHistory.map((pmt) => (
                      <tr key={pmt.id}>
                        <td className="text-muted">
                          {moment(pmt.payment_date || pmt.created_at).format('DD MMM YYYY, hh:mm A')}
                        </td>
                        <td>
                          <code className="text-dark font-weight-bold">
                            {pmt.payu_txnid || pmt.transaction_id || `TXN-${pmt.id}`}
                          </code>
                        </td>
                        <td>
                          <strong>{pmt.plan?.name || 'SaaS Plan'}</strong>
                        </td>
                        <td>
                          <span className="badge bg-light text-dark border">
                            {pmt.billing_cycle || 'monthly'}
                          </span>
                        </td>
                        <td>
                          <strong className="text-dark">
                            ₹ {Number(pmt.amount || 0).toLocaleString('en-IN')}
                          </strong>
                        </td>
                        <td>
                          <Badge color="soft-info" className="text-info border font-size-11 text-uppercase">
                            {pmt.payu_mode || pmt.payment_method || 'PayU'}
                          </Badge>
                        </td>
                        <td>
                          {pmt.status === 'success' || pmt.status === 'COMPLETED' ? (
                            <span className="badge bg-soft-success text-success border border-success font-size-11">
                              <RiCheckboxCircleLine className="me-1" /> Success
                            </span>
                          ) : (
                            <span className="badge bg-soft-danger text-danger border border-danger font-size-11">
                              <RiCloseCircleLine className="me-1" /> {pmt.status}
                            </span>
                          )}
                        </td>
                        <td className="text-end">
                          <Button
                            size="sm"
                            color="light"
                            onClick={() => setSelectedPayment(pmt)}
                            title="View / Print Tax Receipt"
                          >
                            <RiFileDownloadLine className="me-1" /> Invoice
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      </Container>

      {/* ================= PAYU CHECKOUT MODAL ================= */}
      <Modal isOpen={isCheckoutModal} toggle={() => !initiatingPayment && setIsCheckoutModal(false)} size="lg" centered>
        <ModalHeader toggle={() => !initiatingPayment && setIsCheckoutModal(false)}>
          <RiSecurePaymentLine className="text-success me-1 font-size-20" />
          PayU India Secure SaaS Checkout
        </ModalHeader>
        <ModalBody className="p-4">
          {selectedPlan && (
            <div>
              <Row className="g-4">
                {/* Left: Order Summary */}
                <Col md={6}>
                  <div className="p-3 bg-light rounded border">
                    <h6 className="font-weight-bold text-dark mb-2">Order Summary</h6>
                    <div className="d-flex justify-content-between mb-2 font-size-13">
                      <span>Subscription Tier:</span>
                      <strong>{selectedPlan.name}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-2 font-size-13">
                      <span>Billing Interval:</span>
                      <span className="text-uppercase font-weight-bold">{billingCycle}</span>
                    </div>

                    <hr className="my-2" />

                    {(() => {
                      const { basePrice, gst, total } = getTaxBreakdown(calculatePrice(selectedPlan));
                      return (
                        <>
                          <div className="d-flex justify-content-between mb-1 font-size-13 text-muted">
                            <span>Base Plan Price:</span>
                            <span>₹ {basePrice.toLocaleString('en-IN')}</span>
                          </div>
                          <div className="d-flex justify-content-between mb-2 font-size-13 text-muted">
                            <span>GST (18%):</span>
                            <span>₹ {gst.toLocaleString('en-IN')}</span>
                          </div>
                          <div className="d-flex justify-content-between font-size-16 font-weight-bold text-dark pt-2 border-top">
                            <span>Total Payable:</span>
                            <span className="text-primary">₹ {total.toLocaleString('en-IN')}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* Auto Renewal SI Toggle */}
                  <div className="mt-3 p-3 bg-soft-info border border-info rounded">
                    <div className="form-check form-switch mb-1">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="autoDebitCheck"
                        checked={enableAutoDebit}
                        onChange={(e) => setEnableAutoDebit(e.target.checked)}
                      />
                      <Label className="form-check-label font-size-12 font-weight-bold text-dark ms-2" htmlFor="autoDebitCheck">
                        Enable PayU Auto-Debit Standing Instructions (SI)
                      </Label>
                    </div>
                    <small className="text-muted font-size-11 d-block">
                      Automatically renews your subscription at the end of the term so your dental clinic operations are never interrupted. You can cancel anytime.
                    </small>
                  </div>
                </Col>

                {/* Right: Billing Contact Info */}
                <Col md={6}>
                  <h6 className="font-weight-bold text-dark mb-3">Billing & Clinic Information</h6>
                  <FormGroup className="mb-2">
                    <Label className="font-size-12 font-weight-semibold">Clinic / Billing Name *</Label>
                    <Input
                      type="text"
                      value={billingContact.billing_name}
                      onChange={(e) => setBillingContact((p) => ({ ...p, billing_name: e.target.value }))}
                      placeholder="e.g. Apex Dental Hospital"
                    />
                  </FormGroup>

                  <FormGroup className="mb-2">
                    <Label className="font-size-12 font-weight-semibold">Billing Email Address *</Label>
                    <Input
                      type="email"
                      value={billingContact.billing_email}
                      onChange={(e) => setBillingContact((p) => ({ ...p, billing_email: e.target.value }))}
                      placeholder="admin@oralstop.com"
                    />
                  </FormGroup>

                  <FormGroup className="mb-3">
                    <Label className="font-size-12 font-weight-semibold">Billing Phone / WhatsApp Number *</Label>
                    <Input
                      type="tel"
                      value={billingContact.billing_phone}
                      onChange={(e) => setBillingContact((p) => ({ ...p, billing_phone: e.target.value }))}
                      placeholder="9876543210"
                    />
                  </FormGroup>

                  <div className="p-2.5 bg-light rounded font-size-11 text-muted d-flex align-items-center gap-2">
                    <RiLock2Line className="font-size-16 text-success flex-shrink-0" />
                    <span>
                      Transactions are processed with 256-bit SSL encryption via PayU India Payment Gateway.
                    </span>
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setIsCheckoutModal(false)}
            disabled={initiatingPayment}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleProceedPayU}
            disabled={initiatingPayment || !billingContact.billing_name || !billingContact.billing_email}
            className="px-4 font-weight-bold"
          >
            {initiatingPayment ? (
              <>
                <Spinner size="sm" className="me-1" /> Redirecting to PayU...
              </>
            ) : (
              <>
                <RiSecurePaymentLine className="me-1" /> Pay with PayU Gateway
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ================= TAX RECEIPT MODAL ================= */}
      <Modal isOpen={Boolean(selectedPayment)} toggle={() => setSelectedPayment(null)} size="md" centered>
        <ModalHeader toggle={() => setSelectedPayment(null)}>
          SaaS Subscription Tax Invoice
        </ModalHeader>
        <ModalBody className="p-4" id="printable-receipt">
          {selectedPayment && (
            <div>
              <div className="text-center pb-3 border-bottom mb-3">
                <h5 className="font-weight-bold text-dark mb-0">Oralstop Dental Cloud</h5>
                <small className="text-muted">GSTIN: 27AABCO1234F1Z8 | SaaS Platform License</small>
              </div>

              <div className="d-flex justify-content-between mb-2 font-size-12">
                <span className="text-muted">Invoice No:</span>
                <strong>INV-SAAS-{selectedPayment.id}</strong>
              </div>
              <div className="d-flex justify-content-between mb-2 font-size-12">
                <span className="text-muted">PayU Transaction ID:</span>
                <code className="text-dark">{selectedPayment.payu_txnid || selectedPayment.transaction_id || 'N/A'}</code>
              </div>
              <div className="d-flex justify-content-between mb-2 font-size-12">
                <span className="text-muted">Date:</span>
                <span>{moment(selectedPayment.payment_date || selectedPayment.created_at).format('DD MMMM YYYY')}</span>
              </div>
              <div className="d-flex justify-content-between mb-3 font-size-12">
                <span className="text-muted">Payment Mode:</span>
                <span className="text-uppercase">{selectedPayment.payu_mode || 'PayU Online'}</span>
              </div>

              <div className="p-3 bg-light rounded mb-3">
                <div className="d-flex justify-content-between font-size-13 mb-1">
                  <span>{selectedPayment.plan?.name || 'SaaS Subscription Tier'}</span>
                  <span>₹ {Number(selectedPayment.amount || 0).toLocaleString('en-IN')}</span>
                </div>
                <div className="d-flex justify-content-between font-size-11 text-muted">
                  <span>Billing Cycle: {selectedPayment.billing_cycle || 'monthly'}</span>
                  <span>Includes 18% GST</span>
                </div>
              </div>

              <div className="text-center text-muted font-size-11">
                Thank you for choosing Oralstop Dental OPD. This is a computer-generated tax invoice.
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setSelectedPayment(null)}>
            Close
          </Button>
          <Button color="primary" onClick={() => window.print()}>
            <RiFileDownloadLine className="me-1" /> Print Invoice
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default TenantSubscriptionPage;
